import { Button, Dropdown, message } from 'antd';
import { MenuProps } from 'antd';
import { useSelector } from 'react-redux';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import { useToggle } from 'react-use';

import { ConfirmationModal } from '../../../shared/components/confirmation-modal';
import { Feature } from '../../../shared/models/features';
import { translationNamespace } from '../../constants/translation-resources';
import { useDeleteDefinitionMutation } from '../../data/mutations/definition-mutations';
import { Definition } from '../../../shared/models/definition';
import { permissionSelector } from '../../../shared/data/store/selectors/auth-selectors';
import { AppResources } from '../../../shared/constants/app-resources';

interface ActionsProps {
  definition: Definition;
}

export const Actions: FC<ActionsProps> = ({ definition }) => {
  const { t } = useTranslation(translationNamespace);
  const deleteMutation = useDeleteDefinitionMutation();
  const permissions = useSelector(permissionSelector);
  const [showConfirmDeleteModal, toggleShowConfirmDeleteModal] = useToggle(false);

  const remove = useCallback(() => {
    if (definition.id == null) {
      return;
    }

    deleteMutation.mutate(definition.id, {
      onSuccess: () => {
        message.success(t('definitions.shared.deleteSuccessMessage'));
        toggleShowConfirmDeleteModal();
      },
    });
  }, [deleteMutation, t, definition.id, toggleShowConfirmDeleteModal]);

  const items = useMemo<MenuProps['items']>(() => {
    const menuItems: MenuProps['items'] = [];

    if (permissions[AppResources.DEFINITION]['UPDATE']) {
      menuItems.push({
        key: 'edit',
        label: <Link to={`definitions/${definition.id}`}>{t(`${Feature.SHARED}:edit`)}</Link>,
      });
    }
    if (permissions[AppResources.DEFINITION]['DELETE']) {
      menuItems.push({
        key: 'remove',
        onClick: () => toggleShowConfirmDeleteModal(),
        label: t(`${Feature.SHARED}:remove`),
      });
    }

    return menuItems;
  }, [permissions, definition.id, t, toggleShowConfirmDeleteModal]);

  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button type="link" icon={<MenuOutlined />}>
          {t(`${Feature.SHARED}:actions`)}
        </Button>
      </Dropdown>
      <ConfirmationModal
        open={showConfirmDeleteModal}
        title={t(`${Feature.SHARED}:areYouSure`)}
        onCancel={toggleShowConfirmDeleteModal}
        onOk={remove}
        content={t(`${Feature.SHARED}:confirmDeleteMsg`, {
          resourceName: definition.name,
          buttonTitle: t(`${Feature.SHARED}:confirm`),
        })}
      />
    </>
  );
};
